import axios from 'axios';
import { API_CONFIG } from '../config/api.js';

class UserService {
  /**
   * Get admin users for security management
   * @returns {Promise<Object>} Response with admin users data
   */
  static async getSecurityData() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/user/security`, {
        withCredentials: true
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch security data:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch admin users'
      };
    }
  }

  /**
   * Update admin user password
   * @param {string} email - Admin user email
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Promise<Object>} Response with success/error status
   */
  static async updatePassword(email, newPassword, confirmPassword) {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/user/security/admin-update-password`,
        {
          email: email,
          new_password: newPassword,
          confirm_password: confirmPassword
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to update password:', error);

      // Extract validation errors if available
      let errorMessage = 'Failed to update password';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle Pydantic validation errors
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message)
            .join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Validate invitation token
   * @param {string} token - Invitation token to validate
   * @returns {Promise<Object>} Response with success/error status and token details
   */
  static async validateToken(token) {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/auth/validate-token/${token}`, {
        withCredentials: true
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid or expired token'
      };
    }
  }

  /**
   * Set password using reset token
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @returns {Promise<Object>} Response with success/error status
   */
  static async setPassword(token, password) {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/auth/set-password`,
        {
          token: token,
          password: password
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to set password:', error);

      // Extract validation errors if available
      let errorMessage = 'Failed to set password';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle Pydantic validation errors
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message)
            .join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Change password for current authenticated user
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Promise<Object>} Response with success/error status
   */
  static async changeCurrentPassword(currentPassword, newPassword, confirmPassword) {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/user/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to change password'
      };
    }
  }

  /**
   * Validate password strength client-side
   * @param {string} password - Password to validate
   * @returns {Object} Validation result with errors
   */
  static validatePassword(password) {
    const errors = [];

    if (!password || password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/[!@#$%^&*()_+=\-[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    // Check for common weak passwords (mirroring backend validation)
    const commonPasswords = [
      'password', '123456', '123456789', 'qwerty', 'abc123',
      'password123', 'admin', 'letmein', 'welcome', 'monkey'
    ];

    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('Password is too common, please choose a different password');
    }

    // Check for sequential patterns (mirroring backend validation)
    if (/(012|123|234|345|456|567|678|789|890|098|987|876|765|654|543|432|321|210)/.test(password)) {
      errors.push('Password should not contain sequential numbers');
    }

    if (/(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|zyx|yxw|xwv|wvu|vut|uts|tsr|srq|rqp|qpo|pon|onm|nml|mlk|lkj|kji|jih|ihg|hgf|gfe|fed|edc|dcb|cba)/.test(password.toLowerCase())) {
      errors.push('Password should not contain sequential letters');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Delete a user by ID
   * @param {number} userId - User ID to delete
   * @returns {Promise<Object>} Response with success/error status
   */
  static async deleteUser(userId) {
    try {
      const response = await axios.delete(
        `${API_CONFIG.BASE_URL}/user/${userId}`,
        {
          withCredentials: true
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to delete user:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete user'
      };
    }
  }

  /**
   * Get pending invitations
   * @returns {Promise<Object>} Response with pending invitations data
   */
  static async getPendingInvitations() {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/auth/pending-invitations`, {
        withCredentials: true
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch pending invitations:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch pending invitations'
      };
    }
  }

  /**
   * Delete/revoke a pending invitation
   * @param {number} invitationId - Invitation ID to delete
   * @returns {Promise<Object>} Response with success/error status
   */
  static async deleteInvitation(invitationId) {
    try {
      const response = await axios.delete(`${API_CONFIG.BASE_URL}/auth/invitations/${invitationId}`, {
        withCredentials: true
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete invitation'
      };
    }
  }

  /**
   * Resend a pending invitation
   * @param {number} invitationId - Invitation ID to resend
   * @returns {Promise<Object>} Response with success/error status
   */
  static async resendInvitation(invitationId) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/invitations/${invitationId}/resend`, {}, {
        withCredentials: true
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to resend invitation'
      };
    }
  }

  /**
   * Get password strength indicator
   * @param {string} password - Password to check
   * @returns {Object} Strength info
   */
  static getPasswordStrength(password) {
    if (!password) return { level: 'none', text: '', color: '#ccc' };

    let score = 0;

    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+=\-[\]{}|;:,.<>?]/.test(password)) score++;

    const levels = [
      { level: 'very-weak', text: 'Very Weak', color: '#ff4444' },
      { level: 'weak', text: 'Weak', color: '#ff8800' },
      { level: 'fair', text: 'Fair', color: '#ffbb33' },
      { level: 'good', text: 'Good', color: '#00aa00' },
      { level: 'strong', text: 'Strong', color: '#008800' }
    ];

    return levels[Math.min(score, 4)];
  }
}

export default UserService;